import { MachinesListSupportSessionsStatusTypes } from "@common/types/machines-list-types";
import type { SupportSession } from "@src/types/vpn-support-session";
import {
  MachinesListMachineTypes,
  MachinesListCompanyEntry,
} from "@src/types/gateways-with-machines";

type SupportSessionUpdate = Partial<SupportSession> & {
  machine_id?: string;
  status?: string | MachinesListSupportSessionsStatusTypes;
};

type SupportSessionsPayload =
  | SupportSessionUpdate
  | Record<string, SupportSessionUpdate[]>;

type MergeGatewayMachinesDataPropsTypes = {
  machines: Record<string, MachinesListMachineTypes>;
  supportSessionsByMachineIdMap: Map<string, SupportSessionUpdate>;
  isPollingResponse: boolean;
};

type machinesMergeSupportSessionsListDataPropsTypes = {
  allMachinesListData: Record<string, MachinesListCompanyEntry>;
  supportSessionsPayload: SupportSessionsPayload;
  isPollingResponse?: boolean;
  isPostSupportSessionResponse?: boolean;
};

type mergeGatewayMachinesWithSupportSessionsDataReturnType = {
  updatedMachines: Record<string, MachinesListMachineTypes>;
  didMachineChanged: boolean;
};

const getSessionIdentity = (payload?: SupportSessionUpdate): string =>
  payload?.machine_id?.trim() || "";

const buildSupportSessionsMapForPost = (
  supportSessionsPayload: SupportSessionUpdate,
  map: Map<string, SupportSessionUpdate>,
): Map<string, SupportSessionUpdate> => {
  const sessionIdentity = getSessionIdentity(supportSessionsPayload);

  if (!sessionIdentity) {
    return map;
  }

  map.set(sessionIdentity, supportSessionsPayload);
  return map;
};

const buildSupportSessionsMapForPolling = (
  supportSessionsPayload: Record<string, SupportSessionUpdate[]>,
  map: Map<string, SupportSessionUpdate>,
): Map<string, SupportSessionUpdate> => {
  Object.entries(supportSessionsPayload).forEach(
    ([machineIdKey, machineEntryData]) => {
      if (machineEntryData.length === 0) return;

      const sessionUpdate = machineEntryData[0];
      const machineId = sessionUpdate.machine_id?.trim() || machineIdKey.trim();
      if (machineId) map.set(machineId, sessionUpdate);
    },
  );

  return map;
};

const normalizeStatus = (
  status?: string | MachinesListSupportSessionsStatusTypes,
): MachinesListSupportSessionsStatusTypes =>
  (status?.toUpperCase() ||
    MachinesListSupportSessionsStatusTypes.INITIAL) as MachinesListSupportSessionsStatusTypes;

const mergeEntry = (
  machineEntry: MachinesListMachineTypes,
  matchedData: SupportSessionUpdate,
): MachinesListMachineTypes => {
  const status = matchedData?.status;
  return {
    ...machineEntry,
    ...matchedData,
    status: normalizeStatus(status),
  };
};

const removeStatusAndSetInitial = (
  entry: MachinesListMachineTypes,
): MachinesListMachineTypes => ({
  ...entry,
  id: entry.machine_id,
  status: MachinesListSupportSessionsStatusTypes.INITIAL,
  isRequested: false,
  requested_duration: undefined,
  approved_duration: undefined,
  remaining_duration: null,
  technician_user_id: undefined,
  technician_namespace: undefined,
  operator_user_id: undefined,
  visibility: undefined,
  message: undefined,
  websocket_auth_key: undefined,
  websocket_auth_key_expiration: undefined,
  followers_user_ids: undefined,
});

const didMachineChanged = (
  oldVal?: string | null,
  newVal?: string | null,
): boolean => oldVal !== newVal;

const didSupportSessionFieldsChange = (
  machineEntry: MachinesListMachineTypes,
  matchedData: SupportSessionUpdate,
): boolean =>
  Object.entries(matchedData).some(([key, value]) => {
    if (value === undefined) return false;

    return machineEntry[key as keyof MachinesListMachineTypes] !== value;
  });

const getMatchedSupportSessionData = (
  machineEntry: MachinesListMachineTypes,
  supportSessionsByMachineIdMap: Map<string, SupportSessionUpdate>,
): SupportSessionUpdate | null => {
  return supportSessionsByMachineIdMap.get(machineEntry.machine_id) || null;
};

const mergeGatewayMachinesWithSupportSessionsData = ({
  machines,
  supportSessionsByMachineIdMap,
  isPollingResponse,
}: MergeGatewayMachinesDataPropsTypes): mergeGatewayMachinesWithSupportSessionsDataReturnType => {
  let machineChanged = false;

  const updatedMachines: Record<string, MachinesListMachineTypes> = {};

  Object.keys(machines).forEach((machineIdKey) => {
    const machineEntry = machines[machineIdKey];
    const matchedData = getMatchedSupportSessionData(
      machineEntry,
      supportSessionsByMachineIdMap,
    );

    if (matchedData) {
      if (
        didMachineChanged(
          machineEntry.status,
          normalizeStatus(matchedData?.status),
        ) ||
        didMachineChanged(
          machineEntry.remaining_duration,
          matchedData.remaining_duration,
        ) ||
        didSupportSessionFieldsChange(machineEntry, matchedData)
      ) {
        const updatedEntry = mergeEntry(machineEntry, matchedData);
        machineChanged = true;
        updatedMachines[machineIdKey] = updatedEntry;
        return;
      }

      updatedMachines[machineIdKey] = machineEntry;
      return;
    }

    if (
      isPollingResponse &&
      machineEntry.status &&
      machineEntry.status !== MachinesListSupportSessionsStatusTypes.INITIAL
    ) {
      const resetEntry = removeStatusAndSetInitial(machineEntry);
      machineChanged = true;
      updatedMachines[machineIdKey] = resetEntry;
      return;
    }

    updatedMachines[machineIdKey] = machineEntry;
  });

  return { updatedMachines, didMachineChanged: machineChanged };
};

export const machinesMergeSupportSessionsListData = ({
  allMachinesListData,
  supportSessionsPayload,
  isPollingResponse = false,
  isPostSupportSessionResponse = false,
}: machinesMergeSupportSessionsListDataPropsTypes): Record<
  string,
  MachinesListCompanyEntry
> | null => {
  let shouldPassMergedData = false;

  const mergedData: Record<string, MachinesListCompanyEntry> = {
    ...allMachinesListData,
  };

  const map = new Map<string, SupportSessionUpdate>();
  const supportSessionsByMachineIdMap = isPostSupportSessionResponse
    ? buildSupportSessionsMapForPost(
        supportSessionsPayload as SupportSessionUpdate,
        map,
      )
    : buildSupportSessionsMapForPolling(
        supportSessionsPayload as Record<string, SupportSessionUpdate[]>,
        map,
      );

  Object.entries(allMachinesListData).forEach(([entryKey, gatewayOrEntry]) => {
    const { updatedMachines, didMachineChanged } =
      mergeGatewayMachinesWithSupportSessionsData({
        machines: gatewayOrEntry.machines,
        supportSessionsByMachineIdMap,
        isPollingResponse,
      });

    if (didMachineChanged) {
      shouldPassMergedData = true;
      mergedData[entryKey] = {
        ...gatewayOrEntry,
        machines: updatedMachines,
      };
    }
  });

  return shouldPassMergedData ? mergedData : null;
};
